import {EquipmentForParent} from './EquipmentForParent'
import NewEquipment from './NewEquipment'
import {ContentType} from '../../network/Constants'
import Http from '../../network/Http'
import {ApiResponse} from '../../network/response/ApiResponse'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import Equipment from './Equipment'
import {EquipmentSimple} from './EquipmentSimple'
import {ContentsForPagination} from '../shared/ContentsForPagination'
import EquipmentForAdmin from './EquipmentForAdmin'
import {PaginationTableParams} from '../shared/PaginationTableParams'
import UpdateEquipment from './UpdateEquipment'
import {EquipmentWithChildInfo} from './EquipmentWithChildInfo'

export class EquipmentsRepository {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    getEndpoint(condominiumCd: string): string {
        return `/api/admin/condominiums/${condominiumCd}/equipments`
    }

    async getEquipments(condominiumCd: string, params: PaginationTableParams): Promise<EquipmentsForPagination> {
        const queries: string[] = []

        params.sortOrders.forEach((sortOrder) => {
            if (sortOrder.sortDirection !== false) {
                const value: string = encodeURIComponent(`${sortOrder.sortKey}:${sortOrder.sortDirection}`)
                queries.push(`sort-order=${value}`)
            }
        })

        queries.push(`limit=${params.limit}`)

        queries.push(`offset=${params.offset}`)

        let queryString: string = ''
        if (queries.length !== 0) {
            queryString = '?' + queries.join('&')
        }
        const response: ApiResponse<EquipmentsForPagination> =
            await this.http.get(this.getEndpoint(condominiumCd) + queryString)
        const apiResponse: ApiResponse<EquipmentsForPagination> =
            ApiResponse.fromContentsForPagination(response, EquipmentForAdmin.from)
        return apiResponse.details
    }

    async getEquipmentsForParent(condominiumCd: string): Promise<EquipmentForParent[]> {
        const apiResponseJSON: ApiResponse<EquipmentForParent[]> =
            await this.http.get(`${this.getEndpoint(condominiumCd)}/candidate-for-parent`)
        const apiResponse: ApiResponse<EquipmentForParent[]> = ApiResponse.fromArray(
            apiResponseJSON,
            EquipmentForParent.from,
        )
        return apiResponse.details
    }

    async getEquipmentsForFacility(condominiumCd: string): Promise<EquipmentSimple[]> {
        const apiResponseJSON: ApiResponse<EquipmentSimple[]> =
            await this.http.get(
                `${this.getEndpoint(condominiumCd)}/candidate-for-facility`)
        const apiResponse: ApiResponse<EquipmentSimple[]> = ApiResponse.fromArray(
            apiResponseJSON,
            EquipmentSimple.from,
        )
        return apiResponse.details
    }

    async getEquipmentById(condominiumCd: string, equipmentId: number): Promise<EquipmentWithChildInfo> {
        const apiResponseJSON: ApiResponse<EquipmentWithChildInfo> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/equipments/${equipmentId}`)
        const apiResponse: ApiResponse<EquipmentWithChildInfo> = ApiResponse.from(
            apiResponseJSON,
            EquipmentWithChildInfo.from,
        )
        return apiResponse.details
    }

    async registerEquipment(
        condominiumCd: string,
        newEquipment: NewEquipment,
    ): Promise<DetailsWithInformation<Equipment>> {
        const apiResponseJSON: ApiResponse<Equipment> =
            await this.http.post(
                this.getEndpoint(condominiumCd),
                newEquipment,
                ContentType.JSON,
            )
        const apiResponse: ApiResponse<Equipment> = ApiResponse.from(
            apiResponseJSON,
            Equipment.from,
        )
        return new DetailsWithInformation<Equipment>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async updateEquipment(
        condominiumCd: string,
        equipmentId: number,
        updateEquipment: UpdateEquipment,
    ): Promise<DetailsWithInformation<Equipment>> {
        const apiResponseJSON: ApiResponse<Equipment> =
            await this.http.put(
                `${this.getEndpoint(condominiumCd)}/${equipmentId}`,
                updateEquipment,
            )
        const apiResponse: ApiResponse<Equipment> = ApiResponse.from(
            apiResponseJSON,
            Equipment.from,
        )
        return new DetailsWithInformation<Equipment>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async deleteEquipment(condominiumCd: string, equipmentId: number): Promise<boolean> {
        await this.http.delete(`/api/admin/condominiums/${condominiumCd}/equipments/${equipmentId}`)
        return true
    }
}

export type EquipmentsForPagination = ContentsForPagination<EquipmentForAdmin>
